import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** When true, the textarea grows to fit its content (up to `maxRows`). */
  autoResize?: boolean;
  /** Cap the auto-resize growth. Content beyond this scrolls. Defaults to 12. */
  maxRows?: number;
  /** Renders a destructive border + focus ring (e.g. from RHF errors). */
  invalid?: boolean;
}

/**
 * Themed multi-line text input.
 *
 * Uses `.form-textarea` from `index.css` for base styling. When `autoResize`
 * is set, the element grows to fit its content up to `maxRows` and then
 * scrolls internally.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, rows = 3, autoResize = false, maxRows = 12, invalid = false, value, defaultValue, onChange, ...props },
    forwardedRef,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    // Expose the underlying element on the forwarded ref while keeping our own
    // reference for the auto-resize measurement.
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTextAreaElement, []);

    const resize = () => {
      const el = innerRef.current;
      if (!el || !autoResize) return;
      // Reset first so the scrollHeight measurement isn't inflated by the
      // previous height.
      el.style.height = 'auto';
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 20;
      const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const borderY = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      const maxHeight = lineHeight * maxRows + paddingY + borderY;
      const next = Math.min(el.scrollHeight, maxHeight);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    };

    // Measure after paint so fonts/CSS are in place. useLayoutEffect avoids
    // the one-frame flash of the un-resized textarea.
    useLayoutEffect(() => {
      resize();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoResize, maxRows]);

    // Re-measure when the controlled value changes.
    useEffect(() => {
      resize();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <textarea
        ref={innerRef}
        rows={rows}
        value={value}
        defaultValue={defaultValue}
        aria-invalid={invalid || undefined}
        onChange={(event) => {
          onChange?.(event);
          if (autoResize) resize();
        }}
        className={cn(
          'form-textarea',
          'min-h-[2.25rem] px-3 py-2 text-sm leading-relaxed',
          autoResize && 'resize-none overflow-hidden',
          invalid && 'border-destructive focus:border-destructive focus:ring-destructive/25',
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';
